/**
 * Growth & Concentration — Admin → Adoption sub-tab.
 *
 * Momentum (per-day volume by month, multi-seat accounts, new users, live base)
 * and prompt concentration (ranked top-N user share, Gini, cumulative shares)
 * with the overfit guard. Prompt-based only — consumes the existing
 * /api/usage/fetch?raw_events=true feed via useData(); no LLM/reply data needed.
 */
import { useMemo, useState } from 'react';
import { useData } from '../AdoptionData.jsx';
import { companyOf } from '../../../../lib/adoptionAnalytics.js';
import { momentum, userConcentration } from '../../../../lib/adoptionSessionAnalytics.js';
import { HBars } from '../charts.jsx';
import { Kpi, Kpis } from '../Kpi.jsx';

/** Small inline column chart: prompts-per-day by month (latest bar emphasized). */
function MonthBars({ data }) {
  if (!data.length) return null;
  const W = Math.max(360, data.length * 70), H = 200, m = { t: 16, r: 12, b: 28, l: 28 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / data.length, bw = Math.min(46, band * 0.6);
  const max = Math.max(1, ...data.map((d) => d.perDay));
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Prompts per day by month">
      {data.map((d, i) => {
        const h = (d.perDay / max) * ph, x = m.l + band * i + (band - bw) / 2, y = m.t + ph - h;
        const last = i === data.length - 1;
        return (
          <g key={d.month}>
            <rect x={x} y={y} width={bw} height={h} rx="2" fill={last ? '#0d9488' : '#7fd1c8'}>
              <title>{d.month}: {d.perDay}/day ({d.prompts} prompts, {d.activeUsers} active)</title>
            </rect>
            <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize="10" fill="#334">{d.perDay}</text>
            <text x={x + bw / 2} y={H - 10} textAnchor="middle" fontSize="10" fill="#778">{d.month.slice(2)}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function GrowthConcentration() {
  const { events, aliases } = useData();
  const [scope, setScope] = useState('__ALL__');
  const [topN, setTopN] = useState(20);

  const companies = useMemo(
    () => [...new Set(events.map((e) => companyOf(e.user, aliases)))].sort(),
    [events, aliases]
  );
  const ev = useMemo(
    () => (scope === '__ALL__' ? events : events.filter((e) => companyOf(e.user, aliases) === scope)),
    [events, aliases, scope]
  );
  const mo = useMemo(() => momentum(ev, { aliases }), [ev, aliases]);
  const conc = useMemo(() => userConcentration(ev, { aliases, topN }), [ev, aliases, topN]);

  if (!ev.length) return <div className="status">No activity in this window.</div>;

  const lastMonth = mo.perDay[mo.perDay.length - 1];
  const lastNew = mo.newByMonth[mo.newByMonth.length - 1];
  const topCounts = {}; const topOrder = [];
  conc.topList.forEach((u) => { const lbl = `${u.user} · ${u.company}`; topCounts[lbl] = u.count; topOrder.push(lbl); });
  const seatCounts = Object.fromEntries(mo.multiSeat.map((s) => [s.company, s.users]));

  return (
    <>
      <div className="card-head">
        <h1>Growth &amp; concentration</h1>
        <select value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="__ALL__">All companies</option>
          {companies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <h2 className="section-rule">Momentum &amp; expansion</h2>
      <Kpis>
        <Kpi v={lastMonth ? lastMonth.perDay : '—'} l="Prompts / day (latest month)" />
        <Kpi v={`${mo.multiSeatCount}/${mo.totalCompanies}`} l="Multi-seat accounts" />
        <Kpi v={mo.liveBase} l="Live base (active < 4 wks)" />
        <Kpi v={lastNew ? lastNew.new : 0} l="New users (latest month)" />
      </Kpis>
      <section className="card">
        <h3>Prompts per day, by month</h3>
        <MonthBars data={mo.perDay} />
      </section>
      {mo.multiSeat.length > 0 && (
        <section className="card">
          <h3>Accounts with multiple seats</h3>
          <HBars counts={seatCounts} order={mo.multiSeat.map((s) => s.company)} color="#5b8def" />
        </section>
      )}

      <h2 className="section-rule">Prompt concentration — guard against overfit</h2>
      <Kpis>
        <Kpi v={`${conc.top1}%`} l="Top user share" />
        <Kpi v={`${conc.top10}%`} l="Top 10% of users" />
        <Kpi v={conc.gini} l="Gini coefficient" />
        <Kpi v={`${conc.bottomPct}%`} l={`Bottom ${Math.max(0, conc.users - topN)} users`} />
      </Kpis>
      <section className="card">
        <div className="card-head">
          <h3>Share of prompts — top {topN} of {conc.users} users</h3>
          <label className="adopt-days">Top
            <input
              type="number" min="5" max="50" value={topN}
              onChange={(e) => setTopN(Math.max(5, Math.min(50, parseInt(e.target.value || '20', 10))))}
              aria-label="Number of top users to show"
            />
          </label>
        </div>
        <HBars counts={topCounts} order={topOrder} color="#37c98b" denom={conc.total} />
      </section>
      <section className="card">
        <p className="muted" style={{ margin: 0 }}>
          <strong>Guard against overfitting.</strong> The top user ({conc.topUser}, {conc.topCompany}) drives {conc.top1}% of
          all prompts (Gini {conc.gini}). Weight insights by <em>user reach</em>, not prompt volume — validate any
          workflow on ≥5 independent users before building.
        </p>
      </section>
    </>
  );
}
